/**
 * SlushSingleAssetLooping Strategy
 */
import { Decimal } from 'decimal.js';
import { BaseStrategy } from './strategy.js';
import { normalizeSuiObjectId } from '@mysten/sui/utils';
import { ALPHAFI_ORACLE, ALPHALEND_LENDING_PROTOCOL_ID, CLOCK_PACKAGE_ID, GLOBAL_CONFIGS, IMAGE_URLS, } from '../utils/constants.js';
import { planSlushWithdraw, totalSlushXTokens } from '../utils/slushPositions.js';
/**
 * SlushSingleAssetLooping Strategy for leveraged positions with delayed withdrawals
 */
export class SlushSingleAssetLoopingStrategy extends BaseStrategy {
    poolLabel;
    poolObject;
    receiptObjects = [];
    context;
    constructor(poolLabel, poolObject, context) {
        super();
        this.poolLabel = poolLabel;
        this.poolObject = this.parsePoolObject(poolObject);
        this.context = context;
    }
    getPoolLabel() {
        return this.poolLabel;
    }
    getOtherAmount(_amount, _isAmountA) {
        throw new Error('getOtherAmount is not supported for single-asset SlushSingleAssetLooping strategy');
    }
    updateReceipts(receipts) {
        this.receiptObjects = this.parseReceiptObjects(receipts);
    }
    /**
     * Returns alpha mining data - SlushSingleAssetLooping pools do not support alpha mining
     */
    getAlphaMiningData() {
        return {
            poolId: this.poolLabel.poolId,
            accRewardsPerXtoken: [],
            xTokenSupply: '0',
            receipt: null,
        };
    }
    /**
     * Get the exchange rate for xtoken to underlying token ratio
     * Calculated as tokens_invested / xtoken_supply
     */
    exchangeRate() {
        const tokensInvested = new Decimal(this.poolObject.tokensInvested);
        const xtokenSupply = new Decimal(this.poolObject.xTokenSupply);
        if (xtokenSupply.isZero()) {
            return new Decimal(1);
        }
        return tokensInvested.div(xtokenSupply);
    }
    /**
     * Get comprehensive pool data including TVL and APR information
     */
    async getData() {
        const [alphafi, parent, apr] = await Promise.all([
            this.getTvl(),
            this.getParentTvl(),
            this.context.getAprData(this.poolLabel.poolId),
        ]);
        return {
            poolId: this.poolLabel.poolId,
            strategyType: this.poolLabel.strategyType,
            coinType: this.poolLabel.asset.type,
            poolName: this.poolLabel.poolName,
            apr,
            tvl: {
                alphafi,
                parent,
            },
        };
    }
    /**
     * Calculate total value locked using current asset price
     */
    async getTvl() {
        const coinType = this.poolLabel.asset.type;
        const [price, decimals] = await Promise.all([
            this.context.getCoinPrice(coinType),
            this.context.getCoinDecimals(coinType),
        ]);
        const tokenAmount = new Decimal(this.poolObject.tokensInvested).div(new Decimal(10).pow(decimals));
        const usdValue = tokenAmount.mul(price);
        return { tokenAmount, usdValue };
    }
    /**
     * Calculate parent protocol TVL (Alphalend only)
     */
    async getParentTvl() {
        const protocol = this.poolLabel.parentProtocol;
        if (protocol !== 'Alphalend') {
            throw new Error(`Unsupported parent protocol for SlushSingleAssetLooping: ${protocol}`);
        }
        const [tokenAmount, price] = await Promise.all([
            this.context.getAlphaLendTvl(this.poolLabel.asset.type),
            this.context.getCoinPrice(this.poolLabel.asset.type),
        ]);
        return { tokenAmount, usdValue: tokenAmount.mul(price) };
    }
    /**
     * Calculate user's current pool balance including delayed withdrawals
     */
    async getBalance(_userAddress) {
        const [price, decimals] = await Promise.all([
            this.context.getCoinPrice(this.poolLabel.asset.type),
            this.context.getCoinDecimals(this.poolLabel.asset.type),
        ]);
        const totalXTokens = totalSlushXTokens(this.receiptObjects);
        if (totalXTokens === 0n) {
            const withdrawals = await this.getWithdrawalsStatus();
            return {
                tokenAmount: new Decimal(0),
                usdValue: new Decimal(0),
                withdrawals,
            };
        }
        const xTokens = new Decimal(totalXTokens.toString());
        const exchangeRate = this.exchangeRate();
        const tokens = xTokens.mul(exchangeRate).div(new Decimal(10).pow(decimals));
        const withdrawals = await this.getWithdrawalsStatus();
        return {
            tokenAmount: tokens,
            usdValue: tokens.mul(price),
            withdrawals,
        };
    }
    /**
     * Get detailed status of delayed withdrawal requests
     */
    async getWithdrawalsStatus() {
        if (this.receiptObjects.length === 0)
            return [];
        const decimals = await this.context.getCoinDecimals(this.poolLabel.asset.type);
        const currentTime = Date.now();
        // A wallet can hold positions under several caps; claim / cancel resolve the cap per request,
        // so list the requests from every position.
        return this.receiptObjects
            .flatMap((r) => r.withdrawRequests)
            .map((req) => {
            const timeOfUnlock = parseInt(req.timeOfUnlock);
            // Status logic: 1 if current_time < time_of_unlock, otherwise 2 (matches requirements)
            const status = currentTime < timeOfUnlock ? 1 : 2;
            return {
                ticketId: req.id,
                tokenAmount: new Decimal(req.tokenAmount).div(new Decimal(10).pow(decimals)),
                status,
                withdrawalEtaTimestamp: timeOfUnlock,
            };
        });
    }
    /**
     * Parse pool object from blockchain response
     */
    parsePoolObject(response) {
        return this.safeParseObject(() => {
            const fields = this.extractFields(response);
            return {
                id: this.getStringField(fields, 'id'),
                xTokenSupply: this.getNestedField(fields, 'x_token_supply.value') ||
                    this.getNestedField(fields, 'xTokenSupply.value'),
                tokensInvested: this.getStringField(fields, 'tokens_invested') ||
                    this.getStringField(fields, 'tokensInvested'),
                positionCount: this.getNestedField(fields, 'positions.size'),
                positionsTableId: this.getNestedField(fields, 'positions.id'),
                investor: {
                    id: this.getStringField(fields.investor, 'id'),
                    marketId: this.getStringField(fields.investor, 'market_id'),
                    positionCap: {
                        clientAddress: this.getNestedField(fields, 'investor.alphalend_position_cap.client_address'),
                        id: this.getNestedField(fields, 'investor.alphalend_position_cap.id'),
                        imageUrl: this.getNestedField(fields, 'investor.alphalend_position_cap.image_url'),
                        positionId: this.getNestedField(fields, 'investor.alphalend_position_cap.position_id'),
                    },
                },
            };
        }, 'Failed to parse SlushSingleAssetLooping pool object');
    }
    /**
     * Parse investor object (not applicable as it is embedded in pool)
     */
    parseInvestorObject(_) {
        throw new Error('Investor object embedded in pool for SlushSingleAssetLooping');
    }
    /**
     * Parse parent pool object (not applicable)
     */
    parseParentPoolObject(_) {
        throw new Error('Parent pool object not used for SlushSingleAssetLooping');
    }
    /**
     * Parse receipt objects from blockchain responses
     */
    parseReceiptObjects(responses) {
        return responses.map((response, index) => {
            return this.safeParseObject(() => {
                const fields = this.extractFields(response);
                const withdrawRequests = (fields.withdraw_requests.contents || []).map((req) => {
                    const reqFields = this.extractFields(req).value;
                    return {
                        id: this.getStringField(reqFields, 'id'),
                        timeOfRequest: this.getStringField(reqFields, 'time_of_request'),
                        timeOfClaim: this.getStringField(reqFields, 'time_of_claim'),
                        timeOfUnlock: this.getStringField(reqFields, 'time_of_unlock'),
                        status: this.getNumberField(reqFields, 'status'),
                        tokenAmount: this.getStringField(reqFields, 'token_amount'),
                        xTokenAmount: this.getStringField(reqFields, 'x_token_amount'),
                    };
                });
                return {
                    id: this.getStringField(fields, 'id'),
                    positionCapId: this.getStringField(fields, 'position_cap_id'),
                    poolId: this.getStringField(fields, 'pool_id'),
                    coinType: this.getNestedField(fields, 'coin_type.name'),
                    principal: this.getStringField(fields, 'principal'),
                    xTokens: this.getStringField(fields, 'xtokens') ||
                        this.getStringField(fields, 'x_token_balance') ||
                        this.getStringField(fields, 'xTokenBalance'),
                    withdrawRequests,
                };
            }, `Failed to parse SlushSingleAssetLooping receipt object at index ${index}`);
        });
    }
    createPositionCap(tx) {
        const urlBytes = Array.from(new TextEncoder().encode(IMAGE_URLS.SLUSH_POSITION_CAP));
        // module name provided: alphalend_slush_locked_loop_pool
        const target = `${this.poolLabel.packageId}::alphalend_slush_pool::create_position_cap`;
        const positionCap = tx.moveCall({
            target,
            arguments: [tx.pure.vector('u8', urlBytes)],
        });
        return positionCap;
    }
    coinAmountToXToken(amount) {
        const exchangeRate = this.exchangeRate();
        return new Decimal(amount).div(exchangeRate).floor().toString();
    }
    async deposit(tx, options) {
        const alphalendClient = this.context.alphalendClient;
        await alphalendClient.updatePrices(tx, [this.poolLabel.asset.type]);
        await this.collectAndSwapRewards(tx);
        // Get coin object
        const depositCoin = this.context.blockchain.getCoinObject(tx, this.poolLabel.asset.type, options.address, BigInt(options.amount));
        const positionCap = await this.context.getSlushPositionCapForDeposit(options.address, this.poolLabel.poolId);
        const target = `${this.poolLabel.packageId}::alphalend_slush_locked_loop_pool::user_deposit`;
        if (!positionCap) {
            const positionCap = this.createPositionCap(tx);
            tx.moveCall({
                target,
                typeArguments: [this.poolLabel.asset.type],
                arguments: [
                    tx.object(this.poolLabel.versionId),
                    positionCap,
                    tx.object(this.poolLabel.poolId),
                    depositCoin,
                    tx.object(ALPHALEND_LENDING_PROTOCOL_ID),
                    tx.object(CLOCK_PACKAGE_ID),
                ],
            });
            tx.transferObjects([positionCap], options.address);
        }
        else {
            tx.moveCall({
                target,
                typeArguments: [this.poolLabel.asset.type],
                arguments: [
                    tx.object(this.poolLabel.versionId),
                    tx.object(positionCap.id),
                    tx.object(this.poolLabel.poolId),
                    depositCoin,
                    tx.object(ALPHALEND_LENDING_PROTOCOL_ID),
                    tx.object(CLOCK_PACKAGE_ID),
                ],
            });
        }
    }
    async withdraw(tx, options) {
        if (this.receiptObjects.length === 0) {
            throw new Error('No receipt found for withdraw');
        }
        const alphalendClient = this.context.alphalendClient;
        await alphalendClient.updatePrices(tx, [this.poolLabel.asset.type]);
        await this.collectAndSwapRewards(tx);
        // One leg per confirmed position (largest first), each with that position's own cap.
        const legs = planSlushWithdraw(this.receiptObjects, options.withdrawMax ? 'max' : this.coinAmountToXToken(options.amount));
        if (legs.length === 0) {
            throw new Error('Nothing to withdraw');
        }
        const target = `${this.poolLabel.packageId}::alphalend_slush_locked_loop_pool::user_initiate_withdraw`;
        // Each leg opens its own withdraw request on its position.
        for (const leg of legs) {
            tx.moveCall({
                target,
                typeArguments: [this.poolLabel.asset.type],
                arguments: [
                    tx.object(this.poolLabel.versionId),
                    tx.object(leg.positionCapId),
                    tx.object(this.poolLabel.poolId),
                    tx.pure.u64(leg.xTokens),
                    tx.object(ALPHALEND_LENDING_PROTOCOL_ID),
                    tx.object(CLOCK_PACKAGE_ID),
                ],
            });
        }
    }
    /** The confirmed position holding a withdraw request; its cap is the one the contract accepts. */
    findReceiptForWithdrawRequest(withdrawRequestId) {
        const want = normalizeSuiObjectId(withdrawRequestId);
        const receipt = this.receiptObjects.find((r) => r.withdrawRequests.some((w) => normalizeSuiObjectId(w.id) === want));
        if (!receipt) {
            throw new Error(`Withdraw request ${withdrawRequestId} not found in the user's positions`);
        }
        return receipt;
    }
    async claimWithdraw(tx, withdrawRequestId, address) {
        const alphalendClient = this.context.alphalendClient;
        await alphalendClient.updatePrices(tx, [this.poolLabel.asset.type]);
        await this.collectAndSwapRewards(tx);
        const receipt = this.findReceiptForWithdrawRequest(withdrawRequestId);
        const target = `${this.poolLabel.packageId}::alphalend_slush_locked_loop_pool::user_claim_withdraw`;
        const coin = tx.moveCall({
            target,
            typeArguments: [this.poolLabel.asset.type],
            arguments: [
                tx.object(this.poolLabel.versionId),
                tx.object(receipt.positionCapId),
                tx.object(this.poolLabel.poolId),
                tx.pure.id(withdrawRequestId),
                tx.object(ALPHALEND_LENDING_PROTOCOL_ID),
                tx.object(CLOCK_PACKAGE_ID),
            ],
        });
        this.context.blockchain.sendCoinToAddressBalance(tx, this.poolLabel.asset.type, address, coin);
    }
    async cancelWithdraw(tx, withdrawRequestId, _address) {
        const alphalendClient = this.context.alphalendClient;
        await alphalendClient.updatePrices(tx, [this.poolLabel.asset.type]);
        await this.collectAndSwapRewards(tx);
        const receipt = this.findReceiptForWithdrawRequest(withdrawRequestId);
        const target = `${this.poolLabel.packageId}::alphalend_slush_locked_loop_pool::user_cancel_withdraw`;
        tx.moveCall({
            target,
            typeArguments: [this.poolLabel.asset.type],
            arguments: [
                tx.object(this.poolLabel.versionId),
                tx.object(receipt.positionCapId),
                tx.object(this.poolLabel.poolId),
                tx.pure.id(withdrawRequestId),
                tx.object(ALPHALEND_LENDING_PROTOCOL_ID),
                tx.object(CLOCK_PACKAGE_ID),
            ],
        });
    }
    async claimRewards(_tx, _alphaReceipt) {
        return;
    }
    /**
     * Collect rewards from the position and swap them to the base asset
     * Follows the same pattern as SingleAssetLooping strategy
     */
    async collectAndSwapRewards(tx) {
        const [alphaCoin, stsuiCoin, suiCoin, blueCoin, deepCoin, usdcCoin, walCoin, tbtcCoin, suibtcCoin, xaumCoin,] = await this.context.getCoinsBySymbols([
            'ALPHA',
            'stSUI',
            'SUI',
            'BLUE',
            'DEEP',
            'USDC',
            'WAL',
            'tBTC',
            'wBTC',
            'XAUm',
        ]);
        const coinTypes = [
            alphaCoin,
            stsuiCoin,
            suiCoin,
            blueCoin,
            deepCoin,
            usdcCoin,
            walCoin,
            tbtcCoin,
            suibtcCoin,
            xaumCoin,
        ].map((entry) => entry.coinType);
        const alphalendClient = this.context.alphalendClient;
        // Get position ID from the pool object (investor is embedded)
        const positionId = this.poolObject.investor.positionCap.positionId;
        const portfolio = await alphalendClient.getUserPortfolioFromPosition(positionId);
        const rewards = portfolio?.rewardsToClaim;
        if (!rewards) {
            console.log('no rewards for pool id: ', this.poolLabel.poolId);
            return;
        }
        // One oracle update covering every coin the swaps below price, so the PTB carries a
        // single verify + ingest pair instead of one per reward.
        const priceCoinTypes = new Set();
        for (const x of rewards) {
            if (x.coinType == alphaCoin.coinType) {
                priceCoinTypes.add(alphaCoin.coinType);
                priceCoinTypes.add(stsuiCoin.coinType);
                priceCoinTypes.add(suiCoin.coinType);
            }
            else if (coinTypes.includes(x.coinType) && x.coinType != this.poolLabel.asset.type) {
                priceCoinTypes.add(x.coinType);
                priceCoinTypes.add(suiCoin.coinType);
            }
        }
        if (this.poolLabel.asset.name === 'USDSUI') {
            priceCoinTypes.add(this.poolLabel.asset.type);
            priceCoinTypes.add(usdcCoin.coinType);
            priceCoinTypes.add(suiCoin.coinType);
        }
        await alphalendClient.updatePrices(tx, [...priceCoinTypes]);
        // Loop through rewards and collect/swap each one
        for (const x of rewards) {
            if (x.coinType == alphaCoin.coinType) {
                // ALPHA -> stSUI -> SUI
                tx.moveCall({
                    target: `${this.poolLabel.packageId}::alphalend_slush_locked_loop_pool::collect_reward_and_swap_bluefin_v2`,
                    typeArguments: [this.poolLabel.asset.type, alphaCoin.coinType, stsuiCoin.coinType],
                    arguments: [
                        tx.object(this.poolLabel.versionId),
                        tx.object(this.poolLabel.poolId),
                        tx.object(ALPHAFI_ORACLE),
                        tx.object(ALPHALEND_LENDING_PROTOCOL_ID),
                        tx.object(await this.context.getPoolIdBySymbolsAndProtocol('ALPHA', 'stSUI', 'bluefin')),
                        tx.object(GLOBAL_CONFIGS.BLUEFIN),
                        tx.pure.bool(true),
                        tx.pure.bool(false),
                        tx.pure.u64(1000),
                        tx.object(CLOCK_PACKAGE_ID),
                    ],
                });
                tx.moveCall({
                    target: `${this.poolLabel.packageId}::alphalend_slush_locked_loop_pool::collect_reward_and_swap_bluefin_v2`,
                    typeArguments: [this.poolLabel.asset.type, stsuiCoin.coinType, suiCoin.coinType],
                    arguments: [
                        tx.object(this.poolLabel.versionId),
                        tx.object(this.poolLabel.poolId),
                        tx.object(ALPHAFI_ORACLE),
                        tx.object(ALPHALEND_LENDING_PROTOCOL_ID),
                        tx.object(await this.context.getPoolIdBySymbolsAndProtocol('stSUI', 'SUI', 'bluefin')),
                        tx.object(GLOBAL_CONFIGS.BLUEFIN),
                        tx.pure.bool(true),
                        tx.pure.bool(true),
                        tx.pure.u64(10),
                        tx.object(CLOCK_PACKAGE_ID),
                    ],
                });
            }
            else if (coinTypes.includes(x.coinType) && x.coinType != this.poolLabel.asset.type) {
                // Other rewards -> SUI
                tx.moveCall({
                    target: `${this.poolLabel.packageId}::alphalend_slush_locked_loop_pool::collect_reward_and_swap_bluefin_v2`,
                    typeArguments: [this.poolLabel.asset.type, x.coinType, suiCoin.coinType],
                    arguments: [
                        tx.object(this.poolLabel.versionId),
                        tx.object(this.poolLabel.poolId),
                        tx.object(ALPHAFI_ORACLE),
                        tx.object(ALPHALEND_LENDING_PROTOCOL_ID),
                        tx.object(await this.context.getPoolIdByTypesAndProtocol(x.coinType, suiCoin.coinType, 'bluefin')),
                        tx.object(GLOBAL_CONFIGS.BLUEFIN),
                        tx.pure.bool(true),
                        tx.pure.bool(true),
                        tx.pure.u64(10000),
                        tx.object(CLOCK_PACKAGE_ID),
                    ],
                });
            }
        }
        // After collecting all rewards, handle base asset conversion
        // If pool asset is USDSUI, convert SUI -> USDC, then USDC -> USDSUI
        if (this.poolLabel.asset.name === 'USDSUI') {
            // SUI -> USDC
            tx.moveCall({
                target: `${this.poolLabel.packageId}::alphalend_slush_locked_loop_pool::collect_reward_and_swap_bluefin_v2`,
                typeArguments: [this.poolLabel.asset.type, suiCoin.coinType, usdcCoin.coinType],
                arguments: [
                    tx.object(this.poolLabel.versionId),
                    tx.object(this.poolLabel.poolId),
                    tx.object(ALPHAFI_ORACLE),
                    tx.object(ALPHALEND_LENDING_PROTOCOL_ID),
                    tx.object(await this.context.getPoolIdBySymbolsAndProtocol('SUI', 'USDC', 'bluefin')),
                    tx.object(GLOBAL_CONFIGS.BLUEFIN),
                    tx.pure.bool(true),
                    tx.pure.bool(true),
                    tx.pure.u64(10000),
                    tx.object(CLOCK_PACKAGE_ID),
                ],
            });
            // USDC -> USDSUI (base asset)
            tx.moveCall({
                target: `${this.poolLabel.packageId}::alphalend_slush_locked_loop_pool::collect_reward_and_swap_bluefin_v2`,
                typeArguments: [this.poolLabel.asset.type, this.poolLabel.asset.type, usdcCoin.coinType],
                arguments: [
                    tx.object(this.poolLabel.versionId),
                    tx.object(this.poolLabel.poolId),
                    tx.object(ALPHAFI_ORACLE),
                    tx.object(ALPHALEND_LENDING_PROTOCOL_ID),
                    tx.object(await this.context.getPoolIdBySymbolsAndProtocol('USDC', 'USDSUI', 'bluefin')),
                    tx.object(GLOBAL_CONFIGS.BLUEFIN),
                    tx.pure.bool(false),
                    tx.pure.bool(true),
                    tx.pure.u64(10),
                    tx.object(CLOCK_PACKAGE_ID),
                ],
            });
        }
    }
}
//# sourceMappingURL=slushSingleAssetLooping.js.map